import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import * as Yup from "yup";

// Amplify
import Storage from "@aws-amplify/storage";
import { API, graphqlOperation } from "aws-amplify";
import { useHistory } from "react-router-dom";

// Components
import { Formik, Form, Field } from "formik";
import {
  FormControl,
  FormGroup,
  FormLabel,
  Button,
  Alert,
  FormFile,
  ButtonToolbar,
  ButtonGroup,
  ProgressBar,
  Row,
  Col,
  Card,
  Modal,
  Spinner,
} from "react-bootstrap";
import { Redirect } from "react-router-dom";
import { FontSelection } from "./FontSelection";
import PreviewMobileLayoutImages from "./PreviewMobileLayoutImages";
import RippleButton from "./Onboarding/RippleButton";

// Actions
import {
  parseFileToJson,
  createNewMenu,
  createDefaultMenu,
  getMenuById,
  handleUpdateRestaurant,
  parseJsonToFile,
} from "../actions/Menu";

import {
  CLOSE_UPLOAD_MENU_MODAL,
  PUBLISH_MENU_SUCCESS,
  CREATE_MENU_SUCCESS,
  UPDATE_RESTAURANT_SUCCESS,
} from "../actions/ActionTypes";

// Graphql
import { updateMenu, updateMenuSetting } from "../graphql/mutations";
import { parseFile, getRestaurant } from "../graphql/queries";

// Helpers
import {
  formatMenusGeneralSetting,
  getMenuFileFromUrl,
  shouldWriteMenuJson,
} from "../helpers/menu";
import { IMAGES } from "../constants";

// Schema for yup
const validationSchema = Yup.object().shape({
  selectedFileName: Yup.mixed().required("*A file is required"),
  menuName: Yup.string()
    .min(3, "*Menu Name must have at least 3 characters")
    .max(20, "*Menu Name can't be longer than 20 characters")
    .required("*Menu Name is required"),
});

export const MenuUploadModal = ({
  isShow,
  selectedMenu,
  auth: { user },
  dispatch,
  restaurant,
  getMenuById,
  menus,
}) => {
  const getMenuByIdCallback = useCallback(getMenuById, [getMenuById]);
  const [menu, setMenu] = useState(createDefaultMenu());
  const [uploadFile, setUploadFile] = useState({
    name: "",
    file: null,
    jsonContent: null,
  });
  const [loadingStatus, setLoadingStatus] = useState({
    uploadFile: false,
    getMenu: false,
    postMenu: false,
    getFile: false,
  });

  const [previewUrl, setPreviewUrl] = useState();
  const [progressBarPercent, setProgressBarPercent] = useState(0);
  const [error, setError] = useState({
    uploadFile: false,
    getMenu: "",
    postMenu: "",
    preventAddNewMenu: false,
    preventAddNewMenuWithImages: false,
  });
  const [
    previewMenuLayoutWithImages,
    setPreviewMenuLayoutWithImages,
  ] = useState({
    isPreview: false,
    jsonContent: null,
    isUpdatedJsonContent: false,
  });

  // Pull the history object via hook
  const history = useHistory();

  useEffect(() => {
    const loadMenu = async (id) => {
      try {
        setLoadingStatus((prevState) => ({ ...prevState, getMenu: true }));
        await getMenuByIdCallback(id);
      } catch (err) {
        setError((prevState) => ({ ...prevState, getMenu: err.message }));
      } finally {
        setLoadingStatus((prevState) => ({ ...prevState, getMenu: false }));
      }
    };
    if (isShow && selectedMenu) {
      loadMenu(selectedMenu);
    }
  }, [selectedMenu, isShow, getMenuByIdCallback]);

  useEffect(() => {
    if (isShow) {
      const menu = menus.find((menu) => menu.id === selectedMenu);

      if (menu) {
        let fileName = "";

        if (menu.fileLocation) {
          const filePath = menu.fileLocation.split("/");
          const fileNamePath =
            filePath.length > 1
              ? filePath[filePath.length - 1].split("__BW__")
              : "";
          fileName =
            fileNamePath.length > 1
              ? fileNamePath[fileNamePath.length - 1]
              : "";

          setLoadingStatus((prevState) => ({ ...prevState, getFile: true }));

          /**
           * Fetch get menu file from menu.fileLocation to render preview if user select edit the menu
           */
          Storage.get(menu.fileLocation)
            .then(async (fileUrl) => {
              fetch(fileUrl)
                .then((metadata) => metadata.blob())
                .then(async (data) => {
                  const file = new File([data], fileName, {
                    type: "application/octet-stream",
                  });

                  const menuJson = await parseFileToJson(file);
                  if (
                    menuJson.length === 0 ||
                    menuJson === "File parsing error, no rows found"
                  ) {
                    console.log("Can not parse file to json data");
                    setLoadingStatus((prevState) => ({
                      ...prevState,
                      getFile: false,
                    }));
                    return;
                  }

                  /**
                   * Parse file to json success -> render the preview with menu-data
                   */
                  setUploadFile({
                    name: fileName,
                    file,
                    jsonContent: menuJson,
                  });
                  setPreviewMenuLayoutWithImages((prev) => ({
                    ...prev,
                    isPreview: menu.menuLayout === "WITH_IMAGES",
                  }));

                  setLoadingStatus((prevState) => ({
                    ...prevState,
                    getFile: false,
                  }));
                })
                .catch((e) => {
                  console.log("Can not parse link to file:", e);
                  setLoadingStatus((prevState) => ({
                    ...prevState,
                    getFile: false,
                  }));
                });
            })
            .catch((e) => {
              console.log("Can not get fileUrl from fileLocation:", e);
              setLoadingStatus((prevState) => ({
                ...prevState,
                getFile: false,
              }));
            });

          setMenu({ ...menu, fileName });
        }
      } else {
        setMenu(createDefaultMenu());
        setLoadingStatus((prevState) => ({
          ...prevState,
          getFile: false,
        }));
        setUploadFile({
          name: "",
          file: null,
          jsonContent: null,
        });
      }
    }
  }, [menus, selectedMenu, isShow]);

  /**
   * Handle event if user upload/change menu item's image
   */
  const handleChangeMenuItemImage = (menuItemIndex, uploadImage) => {
    const updatedMenuItems = (menuItems, menuItemIndex, uploadImage) => {
      return (
        menuItems &&
        menuItems?.length &&
        menuItems.map((menuItem, index) => {
          // Using index as menuItem's id
          if (index === menuItemIndex) {
            return {
              ...menuItem,
              uploadingImage: uploadImage,
              removingImage: null,
            };
          }
          return menuItem;
        })
      );
    };

    setPreviewMenuLayoutWithImages((prevState) => {
      /**
       * Get the jsonContent from upload file if jsonContent of preview file
       * empty
       */
      const jsonContent = prevState.jsonContent
        ? prevState.jsonContent
        : uploadFile.jsonContent;

      return {
        ...prevState,
        isUpdatedJsonContent: true,
        jsonContent: updatedMenuItems(jsonContent, menuItemIndex, uploadImage),
      };
    });
  };

  /**
   * Handle event if user remove menu item's image
   */
  const handleRemoveMenuItemImage = (menuItemIndex, imageUrl) => {
    const updatedMenuItems = (menuItems, menuItemIndex) => {
      return (
        menuItems &&
        menuItems?.length &&
        menuItems.map((menuItem, index) => {
          // Using index as menuItem's id
          if (index === menuItemIndex) {
            return {
              ...menuItem,
              uploadingImage: null,
              removingImage: imageUrl,
            };
          }
          return menuItem;
        })
      );
    };

    setPreviewMenuLayoutWithImages((prevState) => {
      /**
       * Get the jsonContent from upload file if jsonContent of preview file
       * empty
       */
      const jsonContent = prevState.jsonContent
        ? prevState.jsonContent
        : uploadFile.jsonContent;

      return {
        ...prevState,
        isUpdatedJsonContent: true,
        jsonContent: updatedMenuItems(jsonContent, menuItemIndex),
      };
    });
  };

  const handleChangeMenuItemImageCallback = useCallback(
    handleChangeMenuItemImage,
    [uploadFile.jsonContent]
  );

  const handleRemoveMenuItemImageCallback = useCallback(
    handleRemoveMenuItemImage,
    [uploadFile.jsonContent]
  );

  if (!user) {
    return <Redirect to="/" />;
  }

  function handleMenuNameChange(value) {
    setMenu((prev) => ({ ...prev, name: value }));
  }

  async function download() {
    const link = document.createElement("a");
    link.target = "_blank";
    if (menu?.fileLocation) {
      const file = await Storage.get(menu?.fileLocation);
      link.href = file;
    } else {
      link.href = `${process.env.PUBLIC_URL}/${
        menu?.menuLayout === "WITH_IMAGES"
          ? "template-images.xlsx"
          : "template.xlsx"
      }`;
    }
    link.click();
  }

  async function handleMenuFileChange(e) {
    // Prevent user from advancing until the new file uploaded finishes
    e.preventDefault();

    setLoadingStatus((prevState) => ({ ...prevState, uploadFile: true }));
    setError((prevState) => ({ ...prevState, uploadFile: false }));

    try {
      // Parse Excel file to determine if valid items exist
      const file = e.target.files[0];
      const fileName = file.name;
      const menuJson = await parseFileToJson(file);
      if (
        menuJson.length === 0 ||
        menuJson === "File parsing error, no rows found"
      ) {
        setError((prevState) => ({ ...prevState, uploadFile: true }));
        setLoadingStatus((prevState) => ({ ...prevState, uploadFile: false }));
        setUploadFile({
          name: "",
          file: null,
          jsonContent: null,
        });
        setPreviewMenuLayoutWithImages({
          isPreview: false,
          jsonContent: null,
          isUpdatedJsonContent: false,
        });
        return;
      }

      // Update the menu config
      setMenu((prev) => ({
        ...prev,
        fileName,
      }));

      // Reset state of menu upload form
      setUploadFile({
        name: fileName,
        file,
        jsonContent: menuJson,
      });
      setError((prevState) => ({ ...prevState, uploadFile: false }));
      setLoadingStatus((prevState) => ({ ...prevState, uploadFile: false }));
      setPreviewUrl(undefined);
      setPreviewMenuLayoutWithImages({
        isPreview: menu.menuLayout === "WITH_IMAGES",
        jsonContent: menuJson,
        isUpdatedJsonContent: false,
      });
    } catch (e) {
      console.log("No usable menu items found.", e);
      setError((prevState) => ({ ...prevState, uploadFile: true }));
      setLoadingStatus((prevState) => ({ ...prevState, uploadFile: false }));
    }
  }

  /**
   * Previews or Publishes menus
   * If its a new menu, it is created and store in menu model.
   * Else menu and menuSettings gets updated with new changes only if isPreview is false
   * isPreview => if true, method will generate a -pre url, changes are not stored in model.
   * isPreview intends to display how menu will look but it won't modify prod version of the menu
   * isPreview => false, method will generate a -prod url, changes are stored in model
   *
   * Method generates and url with the menus, if menu was created in phase 1 (single menu approach)
   * the url is created with menuId else is created with restaurantId
   * url is build http:/bw-winelist-website-{env}-website-us-west-2.amazonaws.com/
   * url supports menuId as well, if menuId is in the url that menu will be selected
   * i.e http:/bw-winelist-website-{env}-website-us-west-2.amazonaws.com/{menuId}
   *
   * Method logic summary:
   * 1. Reads the selected file or it uses the latest imported file
   * 2. Validate if selected file contents are ok. (Display message is not)
   * 3. Put selected file to restaurant bucket folder
   * 4. If its a new menu, create menu and menuSettings
   * 5. Add menu to menusPosition at the end of the array
   * 6. Generates generalInfo.json with restaurant and menu-setting info.
   * 7. Put generalInfo.json to restaurant bucket folder
   * 8. Put menu-{id}.json to restaurant bucket folder
   * 9. Put existing menu-{id}.json to restaurant bucket folder
   * 10.Call parseFile () it'll copy *.json files to pre or prod folders
   * 11.Update restaurant, menu and menuSettings model (not in preview)
   *
   * NOTE: paidMenus. by default is 1. This value is changed in another app or manually in table
   * There're several test cases to consider:
   * 1. Modify a menu (created in phase 1 - single menu approach)
   * 2. Add a new menu with (having a menu created in phase 1 - single menu approach)
   * 3. Add a new menu (new account)
   * 4. Modify menus.
   */
  const handleSubmitMenu = async ({ isPreview, menuName, menuLayout }) => {
    let interval;
    let currentRestaurantId = restaurant.id;
    let menuProgressing = menu;
    let latestRestaurantData = null;
    let fileLocation = null;

    try {
      //  Checking is uploaded menu-file or not
      if (!uploadFile.file && !menu?.fileLocation) {
        setError((prevState) => ({
          ...prevState,
          postMenu: "File is required!",
        }));
        return;
      }

      // Reset error message, set loading status and start running progress bar
      setLoadingStatus((prevState) => ({
        ...prevState,
        postMenu: true,
      }));
      setError((prevState) => ({
        ...prevState,
        postMenu: "",
        uploadFile: "",
        preventAddNewMenu: false,
        preventAddNewMenuWithImages: false,
      }));

      /**
       * Before do publish menu, request get restaurant's data
       * to make sure restaurant data is latest
       */
      const restaurantData = await API.graphql(
        graphqlOperation(getRestaurant, { id: currentRestaurantId })
      );

      latestRestaurantData = restaurantData.data.getRestaurant;

      const {
        id,
        logoKey,
        displayDate,
        displayLogo,
        introduction,
        displayCurrencySymbol,
        menus,
        migratedMenuId,
        backgroundImageKey,
        displayBackground,
        dateText,
        descriptionText,
        restaurantName,
        paidMenuImages,
        bookmarksEnabled,
        zoomDisabled,
        loadSectionOnly,
      } = latestRestaurantData;

      // Get the menusPosition fallback to empty array
      let menusPosition = latestRestaurantData.menusPosition || [];

      // Get fallback paidMenus if paidMenus is null or undefined
      const paidMenus =
        latestRestaurantData.paidMenus === undefined ||
        latestRestaurantData.paidMenus === null
          ? 1
          : latestRestaurantData.paidMenus;

      /**
       * NOTES:
       * Check "paidMenus" property against menus created to prevent adding new menus
       */
      if (!selectedMenu && menus.items.length >= paidMenus) {
        setLoadingStatus((prevState) => ({
          ...prevState,
          postMenu: false,
        }));

        setError((prevState) => ({
          ...prevState,
          preventAddNewMenu: true,
        }));

        return;
      }

      /**
       * NOTES:
       * Check "paidMenuImages" property to prevent adding new menu with images
       */
      if (!paidMenuImages && menuLayout === "WITH_IMAGES") {
        setLoadingStatus((prevState) => ({
          ...prevState,
          postMenu: false,
        }));

        setError((prevState) => ({
          ...prevState,
          preventAddNewMenuWithImages: true,
        }));

        return;
      }

      /**
       * Handle submit the menu
       */
      interval = setInterval(() => {
        setProgressBarPercent((progressBarPercent) =>
          progressBarPercent < 100 ? progressBarPercent + 5 : 100
        );
      }, 1000);

      // Reset the preview Url if we in preview mode
      if (isPreview) {
        setPreviewUrl(null);
      }

      // Check if menu was migrated from phase 1 (single menu approach)
      // If use menuId as url else use restaurantId
      const folderId = migratedMenuId ? migratedMenuId : id;
      const folderName = `${folderId}-${isPreview ? "pre" : "prod"}`;

      /**
       * If user uploaded new menu item image:
       * - Do upload ALL updated images to S3 bucket then get "imageUrl" and update json content
       * - Generator new `.xlsx` file from updated json content
       * - Upload generator `.xlsx` file to S3 bucket
       *
       * If user do not uploaded new menu item image:
       * - Upload `.xlsx` file to S3
       */
      let file;

      // Checking if user updated new menu item image (uploaded new menu item image or delete menu item image)
      if (previewMenuLayoutWithImages.isUpdatedJsonContent) {
        /**
         * Function: menuItemsWithImagesUpdated
         *
         * Do upload ALL updated images to S3 bucket
         * Wait until all updated images were uploaded to S3 bucket successfully (using promise-all)
         *   -> generator new `.xlsx` file with "imageUrl" field
         *
         */
        const menuItemsWithImagesUpdated = async () =>
          await Promise.all(
            previewMenuLayoutWithImages.jsonContent.map(
              async (menuItem, index) => {
                /**
                 * Checking is user uploaded/updated new menu item images
                 */
                if (menuItem.uploadingImage) {
                  // Using time-stamp to make sure put the new image will not override the image was exist before
                  const imgUploaded = await Storage.put(
                    `${folderName}/menu-${index}-${new Date().getTime()}.png`,
                    menuItem.uploadingImage
                  );

                  /**
                   * const imageUrl = await Storage.get(imgUploaded.key);
                   *
                   * The imageUrl get here is a temporal url that only works within the session.
                   * if you log out and log in again it won’t work.
                   * -> So lets store the key and get the file url when the preview menu is opened
                   */

                  // Get the image key
                  const imgUploadedKey = imgUploaded?.key;

                  return {
                    ...menuItem,
                    imageUrl: imgUploadedKey,
                    uploadingImage: null,
                    removingImage: null,
                  };
                }

                /**
                 * Checking is user removed menu item images
                 */
                if (menuItem.removingImage) {
                  // IMPORTANT NOTE: Only remove image if user publish menu
                  if (!isPreview) {
                    await Storage.remove(menuItem.removingImage);
                  }

                  return {
                    ...menuItem,
                    imageUrl: null,
                    uploadingImage: null,
                    removingImage: null,
                  };
                }

                return menuItem;
              }
            )
          );

        // Get the return of json-content after uploaded all images
        const updateJsonContent = await menuItemsWithImagesUpdated();
        /**
         * Generator new `.xlsx` file from new json-content with "imageUrl" field
         * -> Delete unusual fields: [uploadingImage, removingImage] of object "updateJsonContent"
         */
        file = parseJsonToFile(
          menu?.fileName || "template-images.xlsx",
          updateJsonContent,
          ["uploadingImage", "removingImage"]
        );
      } else {
        file =
          uploadFile && uploadFile.file
            ? uploadFile.file
            : await getMenuFileFromUrl(
                await Storage.get(menu?.fileLocation),
                menu?.fileName || "template.xlsx"
              );
      }

      // Parse the file to json object
      const jsonContent = await parseFileToJson(file);

      // Catching error while parse file
      if (
        jsonContent.length === 0 ||
        jsonContent === "File parsing error, no rows found"
      ) {
        throw new Error("Generator error ");
      }

      /**
       * Images will be stored in the website in images folder
       * However
        jsonContent.forEach((val) => {
          if (val.imageUrl) {
            let imageUrl = val.imageUrl;
            let index = imageUrl.lastIndexOf("/");
            val.imageUrl = "images" + imageUrl.substring(index);
          }
        });
       */

      // IMPORTANT: Push file to S3 store
      fileLocation = await Storage.put(
        folderName + "/" + new Date().getTime() + "__BW__" + file.name,
        file,
        {
          contentType: file.type,
        }
      );

      /**
       * If don't exist the "selectedMenu" property mean we are creating new menu
       * Do create new menu
       */
      if (!selectedMenu) {
        // Create new menu
        const data = await createNewMenu({
          menuRestaurantId: id,
          name: menuName,
          displayDate,
          displayLogo,
          introduction,
          displayCurrencySymbol,
          menuSetting: menuProgressing.menuSetting,
          isPublished: !isPreview,
          fileLocation: fileLocation.key,
          menuLayout,
        });

        dispatch({ type: PUBLISH_MENU_SUCCESS, payload: data });

        const createdMenu = {
          ...data,
          fileName: uploadFile.name,
        };

        dispatch({
          type: CREATE_MENU_SUCCESS,
          payload: createdMenu,
        });

        menuProgressing = createdMenu;
      }

      let logoUrl = undefined;
      let backgroundImageUrl = undefined;

      // If there's logo and setting is on
      if (displayLogo && logoKey) {
        logoUrl = "./logo.png";
      }

      // If there's background image and setting is on
      if (displayBackground && backgroundImageKey) {
        backgroundImageUrl = "./backgroundImage.png";
      }

      const updatedMenus = formatMenusGeneralSetting(menus.items, {
        ...menuProgressing,
        isPublished: !isPreview,
      });

      // Add new menu to the end of position array
      if (menusPosition.indexOf(menuProgressing.id) === -1) {
        menusPosition = [...menusPosition, menuProgressing.id];
      }

      // Upload general setting config
      const generalInfo = {
        // Restaurant setting
        id,
        name: latestRestaurantData.name,
        introduction,
        displayDate,
        logoUrl,
        publishedDate: new Date(),
        backgroundImageUrl,
        displayBackground,
        displayCurrencySymbol,
        dateText,
        descriptionText,
        restaurantName,
        displayLogo,
        menusPosition,
        bookmarksEnabled,
        loadSectionOnly,
        zoomDisabled,
        // Menus setting
        menus: updatedMenus,
      };

      await Storage.put(
        folderName + "/generalInfo.json",
        JSON.stringify(generalInfo),
        {
          contentType: "application/json",
        }
      );

      let menuItem = null;
      let jsonKeys = [];
      jsonKeys.push(`public/${folderName}/menu-${menuProgressing.id}.json`);
      // Integrated menus: keep the menu json the integration wrote
      if (
        await shouldWriteMenuJson(
          jsonContent,
          `${folderName}/menu-${menuProgressing.id}.json`
        )
      ) {
        await Storage.put(
          `${folderName}/menu-${menuProgressing.id}.json`,
          JSON.stringify(jsonContent),
          {
            contentType: "application/json",
          }
        );
      }

      for (menuItem of latestRestaurantData.menus.items) {
        if (menuItem.id !== menuProgressing.id) {
          const fileLocationSplit = menuItem.fileLocation.split("/");
          let fileName =
            fileLocationSplit.length > 1 ? fileLocationSplit[1] : "";
          let file = await getMenuFileFromUrl(
            await Storage.get(menuItem.fileLocation),
            fileName
          );

          // Parse the file to json object
          let _jsonContent = await parseFileToJson(file);

          console.log(
            "DEBUG PURPOSE Do parse file here",
            menuItem,
            _jsonContent
          );
          /**
           * Images will be stored in the website in images folder
          _jsonContent.forEach((val) => {
            if (val.imageUrl) {
              let imageUrl = val.imageUrl;
              let index = imageUrl.lastIndexOf("/");
              val.imageUrl = "images" + imageUrl.substring(index);
            }
          });
           */

          if (
            await shouldWriteMenuJson(
              _jsonContent,
              `${folderName}/menu-${menuItem.id}.json`
            )
          ) {
            await Storage.put(
              `${folderName}/menu-${menuItem.id}.json`,
              JSON.stringify(_jsonContent),
              {
                contentType: "application/json",
              }
            );
          }

          jsonKeys.push(`public/${folderName}/menu-${menuItem.id}.json`);
        }
      }

      // Call parseFile to create/update preview bucket
      const parsed = await API.graphql(
        graphqlOperation(parseFile, {
          folderName,
          generalKey: `public/${folderName}/generalInfo.json`,
          jsonKey: jsonKeys,
          logoKey: logoKey ? `public/${logoKey}` : null,
          backgroundImageKey: backgroundImageKey
            ? `public/${backgroundImageKey}`
            : null,
        }),
        []
      );

      // Show the url of menu if in preview mode
      if (isPreview) {
        // After upload the file to "pre" success. Set the progress bar to 100%
        setProgressBarPercent(100);
        // Add # and menuId to display preview of selected menu
        setPreviewUrl(`${parsed.data.parseFile}/#/${menuProgressing.id}`);
        return;
      }

      /**
       * Update the position of menus in restaurant when create new menu
       */
      let restaurantInput = {
        id,
        menusPosition,
      };
      if (parsed.data.parseFile !== "error") {
        restaurantInput.restaurantUrl = parsed.data.parseFile;
      }
      const updatedRestaurant = await handleUpdateRestaurant(restaurantInput);

      dispatch({
        type: UPDATE_RESTAURANT_SUCCESS,
        payload: updatedRestaurant,
      });

      setMenu((prevState) => ({
        ...menuProgressing,
        fileName: prevState.fileName,
      }));

      /**
       * Update the data of menu
       */
      const menuUrl = `${latestRestaurantData.restaurantUrl}/#/menu-${menuProgressing.id}`;

      const inputMenu = {
        id: menuProgressing.id,
        fileLocation: fileLocation.key,
        menuUrl,
        name: menuName,
        isPublished: true,
        menuLayout,
      };

      const { data } = await API.graphql(
        graphqlOperation(updateMenu, { input: inputMenu })
      );

      const publishedMenu = data.updateMenu;
      dispatch({ type: PUBLISH_MENU_SUCCESS, payload: publishedMenu });

      const inputMenuSettings = {
        id: menuProgressing.menuSetting.id,
        displayDate,
        displayLogo,
        introduction,
        logoKey,
        displayCurrencySymbol,
        headerOne: menuProgressing.menuSetting.headerOne,
        headerTwo: menuProgressing.menuSetting.headerTwo,
        headerThree: menuProgressing.menuSetting.headerThree,
        headerFour: menuProgressing.menuSetting.headerFour,
        normalText: menuProgressing.menuSetting.normalText,
        subtext: menuProgressing.menuSetting.subtext,
      };

      await API.graphql(
        graphqlOperation(updateMenuSetting, { input: inputMenuSettings })
      );

      handleClose();
      setPreviewMenuLayoutWithImages({ isPreview: false, jsonContent: null });
      setUploadFile({
        name: "",
        file: null,
        jsonContent: null,
        isUpdatedJsonContent: false,
      });

      // Go to the QR-code page after publish the menu success
      history.push(`/qr-code/`);
    } catch (err) {
      console.log("DEBUG PURPOSE Err =>", err);
      setError((prevState) => ({
        ...prevState,
        postMenu: "Can not publish menu. Please try again!",
        uploadFile:
          err.message && err.message === "Generator error"
            ? err.message
            : false,
      }));
    } finally {
      setLoadingStatus((prevState) => ({
        ...prevState,
        postMenu: false,
      }));
      clearInterval(interval);
      setProgressBarPercent(0);
    }
  };

  function handleUpdateFontColor(settingProperty) {
    setMenu((prevState) => ({
      ...prevState,
      menuSetting: { ...prevState.menuSetting, ...settingProperty },
    }));
  }

  function handleClose() {
    setPreviewUrl(null);
    setError({ uploadFile: false, getMenu: "", postMenu: "" });
    setMenu(createDefaultMenu());
    setUploadFile({
      name: "",
      file: null,
      jsonContent: null,
      isUpdatedJsonContent: false,
    });
    setPreviewMenuLayoutWithImages({ isPreview: false, jsonContent: null });
    dispatch({ type: CLOSE_UPLOAD_MENU_MODAL });
  }

  function onSelectMenuLayout(event) {
    event.persist();
    const menuLayout = event.target.value;
    setMenu((prevState) => ({ ...prevState, menuLayout }));
    setPreviewMenuLayoutWithImages({
      isPreview: menuLayout === "WITH_IMAGES",
      jsonContent: uploadFile.jsonContent,
    });
  }

  return (
    <Modal
      animation
      show={isShow}
      backdrop="static"
      keyboard={false}
      onHide={handleClose}
      size="lg"
      dialogClassName="setting-modal"
    >
      <Modal.Header>
        <Modal.Title className="title-setting">
          {selectedMenu ? "Edit" : "Add"} Menu
        </Modal.Title>
        {loadingStatus.getMenu && (
          <Spinner
            as="span"
            animation="border"
            role="status"
            className="ml-1"
            aria-hidden="true"
          />
        )}
        <img
          src={IMAGES.CLOSE_ICON}
          alt="close-icon"
          width="44"
          height="44"
          onClick={handleClose}
        />
      </Modal.Header>
      <Modal.Body>
        {error.getMenu ? (
          <Alert variant="danger">{error.getMenu}</Alert>
        ) : (
          <Formik
            enableReinitialize
            initialValues={{
              selectedFileName: menu?.fileName || uploadFile.name || "",
              menuName: menu?.name || "",
              isPreview: false,
              menuLayout: menu?.menuLayout || "DEFAULT",
            }}
            validationSchema={validationSchema}
            onSubmit={handleSubmitMenu}
          >
            {({ errors, touched, setFieldValue, handleSubmit }) => (
              <Form>
                <Row>
                  <Col>
                    <Alert variant="warning">
                      Export the template by clicking on{" "}
                      <strong>Download</strong> , then modify and import it to
                      update your menu.
                    </Alert>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <Field name="menuName">
                      {({ field }) => (
                        <FormGroup
                          controlId="menuName"
                          className="setting-form-group"
                        >
                          <FormControl
                            type="text"
                            placeholder="Menu Name"
                            value={field.value}
                            onChange={(e) => {
                              handleMenuNameChange(e.target.value);
                              field.onChange(e);
                            }}
                            isValid={touched.menuName && !errors.menuName}
                            isInvalid={touched.menuName && errors.menuName}
                            className="setting-input"
                            data-testid="menu-name"
                          />
                          <FormControl.Feedback type="invalid">
                            {errors.menuName}
                          </FormControl.Feedback>
                        </FormGroup>
                      )}
                    </Field>
                    <Row className="mt-2 mb-2">
                      <Col>
                        <Field name="menuLayout" as="select">
                          {({ field }) => (
                            <FormGroup controlId="menuLayout">
                              <FormLabel className="setting-menu-label">
                                Select Layout
                              </FormLabel>
                              <FormControl
                                as="select"
                                custom
                                value={field.value}
                                onChange={(e) => {
                                  onSelectMenuLayout(e);
                                  field.onChange(e);
                                }}
                                className="setting-menu-select"
                              >
                                <option value="DEFAULT">Digital menu</option>
                                <option value="WITH_IMAGES">
                                  Digital menu with images
                                </option>
                              </FormControl>
                            </FormGroup>
                          )}
                        </Field>
                      </Col>
                    </Row>
                    <hr />
                    <FontSelection
                      setting={menu.menuSetting}
                      handleUpdateSetting={handleUpdateFontColor}
                      isMenuSetting
                    />
                  </Col>
                </Row>
                <Row className="mt-2 mb-2">
                  <Col>
                    <FormLabel className="setting-menu-label">
                      Export Menu
                    </FormLabel>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <Button
                      onClick={download}
                      download
                      variant="outline-dark download-template"
                      data-testid="download-template"
                    >
                      Download
                      <img
                        src={IMAGES.DOWNLOAD_ICON}
                        alt="download"
                        height={22}
                        width={22}
                      />
                    </Button>
                  </Col>
                </Row>
                <Row className="mt-4 mb-2">
                  <Col>
                    <FormLabel className="setting-menu-label">
                      Import Menu
                    </FormLabel>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    {/* <Field name="selectedFileName"> */}
                    <FormGroup controlId="selectedFileName">
                      <FormFile id="menu-file" custom>
                        <div className="d-flex align-items-center menu-upload-file">
                          <label
                            className="fileInput-label"
                            htmlFor="menu-file"
                          >
                            Choose file
                          </label>
                          <span
                            className="fileInput-name text-truncate"
                            htmlFor="menu-file"
                          >
                            {uploadFile.name ||
                              menu.fileName ||
                              "Select a file"}
                          </span>
                        </div>
                        <FormFile.Input
                          name="selectedFile"
                          onClick={(event) => {
                            // Reset the value so that onChange will fire again if user uploads the same file
                            event.target.value = "";
                          }}
                          onChange={(event) => {
                            const file = event.currentTarget.files[0];

                            if (file) {
                              setFieldValue("selectedFileName", file.name);
                              handleMenuFileChange(event);
                            }
                          }}
                          isValid={
                            touched.selectedFileName && !errors.selectedFileName
                          }
                          isInvalid={
                            touched.selectedFileName && errors.selectedFileName
                          }
                          accept=".csv, .xlsx"
                          data-testid="select-file"
                          className="d-none"
                        />

                        <FormControl.Feedback type="invalid">
                          {errors.selectedFileName}
                        </FormControl.Feedback>
                      </FormFile>
                    </FormGroup>
                  </Col>
                </Row>

                {/* Status section for file */}
                <Row>
                  {error.uploadFile && (
                    <Col>
                      <Alert variant="danger">
                        Imported file has <strong>errors</strong>, please try
                        again.
                      </Alert>
                    </Col>
                  )}
                  {loadingStatus.uploadFile && (
                    <Col>
                      <ProgressBar
                        animated
                        variant="success"
                        now={progressBarPercent}
                        label={`${progressBarPercent}%`}
                      />
                      <p>Please wait while we validate the file...</p>
                    </Col>
                  )}
                </Row>
                <Row>
                  {uploadFile.file &&
                    !loadingStatus.uploadFile &&
                    !error.uploadFile && (
                      <Col className="text-center upload-success-message">
                        File looks Good! Please click on Preview or Publish
                      </Col>
                    )}
                </Row>
                <hr />
                {loadingStatus.getFile && (
                  <Row className="justify-content-center mb-2">
                    <Spinner
                      as="span"
                      animation="border"
                      role="status"
                      className="ml-1"
                      aria-hidden="true"
                    />
                  </Row>
                )}

                <Row>
                  {previewMenuLayoutWithImages.isPreview &&
                  (previewMenuLayoutWithImages.jsonContent ||
                    uploadFile.jsonContent) ? (
                    <PreviewMobileLayoutImages
                      jsonContent={
                        previewMenuLayoutWithImages.jsonContent ||
                        uploadFile.jsonContent
                      }
                      menuSetting={menu.menuSetting}
                      handleChangeImage={handleChangeMenuItemImageCallback}
                      handleRemoveImage={handleRemoveMenuItemImageCallback}
                    />
                  ) : null}
                </Row>

                <Row className="mt-3">
                  {loadingStatus.postMenu && (
                    <Col>
                      <ProgressBar
                        animated
                        variant="success"
                        now={progressBarPercent}
                        label={`${progressBarPercent}%`}
                      />
                      <p className="font-italic text-muted">
                        Please wait while we generate your menu.
                      </p>
                    </Col>
                  )}
                </Row>
                {previewUrl && (
                  <div>
                    <Row className="mt-4 mb-2">
                      <Col>
                        <FormLabel className="text-muted font-italic">
                          Preview was generated, use this link to view:
                        </FormLabel>
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <Card body>
                          <a
                            href={previewUrl}
                            className="font-weight-bold maroon"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {previewUrl}
                          </a>
                        </Card>
                      </Col>
                    </Row>
                  </div>
                )}
                <Row>
                  {error.preventAddNewMenu && (
                    <Col>
                      <Alert variant="warning">
                        Please contact our support team to{" "}
                        <strong>
                          <a href="mailto: support@binwise.com">
                            support@binwise.com
                          </a>
                        </strong>{" "}
                        to add additional menus!
                      </Alert>
                    </Col>
                  )}
                </Row>
                <Row>
                  {error.preventAddNewMenuWithImages && (
                    <Col>
                      <Alert variant="warning">
                        Please contact our support team to{" "}
                        <strong>
                          <a href="mailto: support@binwise.com">
                            support@binwise.com
                          </a>
                        </strong>{" "}
                        to use Digital menu with images!
                      </Alert>
                    </Col>
                  )}
                </Row>
                <Row>
                  {error.postMenu && (
                    <Col>
                      <Alert variant="danger">
                        {error.uploadFile ? (
                          <p>
                            Something went <strong>wrong</strong>, please try
                            importing a different file.
                          </p>
                        ) : (
                          error.postMenu
                        )}
                      </Alert>
                    </Col>
                  )}
                </Row>
                <ButtonToolbar
                  aria-label="Toolbar with button groups"
                  className="justify-content-center"
                >
                  <ButtonGroup className="mr-4" aria-label="Third group">
                    <Button
                      variant="dark"
                      className="fileInput-label setting-button"
                      disabled={
                        loadingStatus.uploadFile || loadingStatus.postMenu
                      }
                      onClick={() => {
                        setFieldValue("isPreview", true);
                        handleSubmit();
                      }}
                    >
                      Preview
                    </Button>
                  </ButtonGroup>
                  <ButtonGroup aria-label="Fourth group">
                    <RippleButton
                      type
                      customStyles="setting-button"
                      onClick={() => {
                        setFieldValue("isPreview", false);
                      }}
                      disabled={
                        loadingStatus.uploadFile || loadingStatus.postMenu
                      }
                    >
                      Publish
                      {loadingStatus.postMenu && (
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          className="ml-1"
                          aria-hidden="true"
                        />
                      )}
                    </RippleButton>
                  </ButtonGroup>
                </ButtonToolbar>
              </Form>
            )}
          </Formik>
        )}
      </Modal.Body>
    </Modal>
  );
};

MenuUploadModal.propTypes = {
  auth: PropTypes.object.isRequired,
  isShow: PropTypes.bool.isRequired,
  selectedMenu: PropTypes.string,
  dispatch: PropTypes.func.isRequired,
  restaurant: PropTypes.object,
  getMenuById: PropTypes.func.isRequired,
  menus: PropTypes.array.isRequired,
};

const mapStateToProps = (state) => {
  const { menus, selectedMenu } = state.menu;

  return {
    auth: state.auth,
    isShow: state.modal.isShowUploadMenuModal,
    selectedMenu,
    menus,
    restaurant: state.restaurant.restaurant,
  };
};
const mapDispatchToProps = (dispatch) => ({
  dispatch,
  ...bindActionCreators({ getMenuById }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(MenuUploadModal);
